import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Globe, ChevronDown, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d8692872-ce9e-4544-878c-3a5b2f1a67b0/artifacts/lff9980v_GTIC%20STUDIO%20LOGO2.png";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const { totalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/about', label: t('nav_about') },
    { to: '/services', label: t('nav_services') },
    { to: '/projects', label: t('nav_projects') },
    { to: '/daily-life', label: t('nav_daily_life') },
    { to: '/training', label: t('nav_training') },
    { to: '/careers', label: t('nav_careers') },
    { to: '/shop', label: t('nav_shop') },
    { to: '/partners', label: t('nav_partners') },
    { to: '/contact', label: t('nav_contact') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-white'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="GTIC SARL"
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'text-[#0B6AB3]'
                    : 'text-gray-700 hover:text-[#0B6AB3]'
                }`}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1" data-testid="language-switcher">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline uppercase">{language}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="lang-fr">
                  🇫🇷 Français
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cart */}
            <Link to="/cart" className="relative" data-testid="cart-link">
              <Button variant="ghost" size="sm">
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#E50E24] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </Button>
            </Link>

            {/* Auth */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1" data-testid="user-menu">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(user?.role === 'admin' || user?.role === 'editor') && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="admin-link">{t('nav_admin')}</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm" data-testid="login-link">
                  {t('nav_login')}
                </Button>
              </Link>
            )}

            {/* CTA Button */}
            <Link to="/contact" className="hidden lg:block">
              <Button
                className="bg-[#E50E24] hover:bg-[#c40c1f] text-white"
                data-testid="quote-btn"
              >
                {t('nav_get_quote')}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(link.to)
                    ? 'bg-[#0B6AB3]/10 text-[#0B6AB3]'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t mt-4">
              {!isAuthenticated && (
                <Link to="/login" className="block px-3 py-2 text-gray-700">
                  {t('nav_login')}
                </Link>
              )}
              <Link to="/contact">
                <Button className="w-full mt-2 bg-[#E50E24] hover:bg-[#c40c1f] text-white">
                  {t('nav_get_quote')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
